import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'
import { ls } from '@/ls'
import { authStore } from '@/stores/auth'
import { subtotalStore } from '@/stores/subtotal'
import { headerStore } from '@/stores/header'
import { Option } from '@/types/completion3/option'

export const completionThreeStore = defineStore('completionThree', () => {
  const loading = ref(false)
  const savingId = ref(0)
  const removingId = ref(0)
  const options = ref<Option[]>([])
  const selectedOptionIds = computed(() => {
    const selectedOptions:number[] = []

    options.value.forEach((option:Option) => {
      if (option.selected) {
        selectedOptions.push(option.id)
      }
    })

    return selectedOptions
  })

  const isDisabled = computed(() => {
    interface OptionStates { [key: string]: boolean; }
    const optionStates:OptionStates = {}

    options.value.forEach((option:Option) => {
      optionStates[option.id.toString()] = option.unavailableWith.some((unavailableWithOptionId:number) => selectedOptionIds.value.includes(unavailableWithOptionId)) ||
        (option.onlyAvailableWith.length ? !option.onlyAvailableWith.some((onlyAvailableWithOptionId:number) => selectedOptionIds.value.includes(onlyAvailableWithOptionId)) : false)
    })
    return optionStates
  })

  const isInternalTestAccount = computed(() => {
    return ls.get('demoAccount').toLowerCase() === authStore().user.email.toLowerCase()
  })

  const phaseState = ref<string>('loading')
  const phaseIsClosed = computed(() => {
    return ['closed', 'processed', 'expired'].includes(phaseState.value) || headerStore().ordersReceived.completion3
  })

  function getOptions () : void {
    loading.value = true
    axios.get(process.env.VUE_APP_API_URL + '/completion3/options').then((r) => {
      options.value = r.data
    }).finally(() => {
      loading.value = false
    })
  }

  function selectOption (selectedOption:Option) : void {
    savingId.value = selectedOption.id
    axios.post(process.env.VUE_APP_API_URL + '/completion3/option/store', { oid: selectedOption.id }).then(() => {
      subtotalStore().getSubtotals()
      options.value.forEach((option:Option) => {
        if (option.id === selectedOption.id) {
          option.selected = true
        }
      })
    }).finally(() => {
      savingId.value = 0
    })
  }

  function unselectOption (unselectedOption:Option) : void {
    removingId.value = unselectedOption.id
    axios.post(process.env.VUE_APP_API_URL + '/completion3/option/remove', { oid: unselectedOption.id }).then(() => {
      subtotalStore().getSubtotals()
      options.value.forEach((option:Option) => {
        if (option.id === unselectedOption.id) {
          option.selected = false
        }

        // Check if there are options which are only available if unselected option is selected.
        if (option.onlyAvailableWith.length && option.onlyAvailableWith.includes(unselectedOption.id)) {
          option.selected = false
          axios.post(process.env.VUE_APP_API_URL + '/completion3/option/remove', { oid: option.id }).then(() => {
            subtotalStore().getSubtotals()
          })
        }
      })
    }).finally(() => {
      removingId.value = 0
    })
  }

  function getPhaseState () : void {
    loading.value = true
    axios.get(process.env.VUE_APP_API_URL + '/phase/2/closed').then((r) => {
      phaseState.value = r.data.status
    })
  }

  return { loading, savingId, removingId, options, selectedOptionIds, isDisabled, isInternalTestAccount, phaseState, phaseIsClosed, getOptions, selectOption, unselectOption, getPhaseState }
})
